import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { InternalNavbar } from "@/components/InternalNavbar"
import { PWAInstallButton } from "@/components/pwa-install-button"
import { LicenseManager } from "@/components/LicenseManager"
import { useLanguage } from "@/hooks/use-language"
import { useNotifications } from "@/hooks/use-notifications"
import { useLicense } from "@/hooks/use-license"
import { useAuth } from "@/contexts/AuthContext"
import { OfflineCache } from "@/lib/offline-cache"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
  Settings as SettingsIcon,
  MessageSquare,
  CheckCircle2,
  XCircle,
  Bell,
  Smartphone,
  TestTube,
  Send,
  Loader2,
  AlertCircle,
  User,
  HelpCircle,
  Save,
  Lock,
  Crown,
  Zap
} from "lucide-react"

export function Settings() {
  const { language } = useLanguage()
  const { user } = useAuth()
  const navigate = useNavigate()
  const { license, licenseType, isActive, isExpired, isTrial, daysUntilExpiry, features } = useLicense(user?.uid)
  const {
    permission,
    token,
    isLoading: notificationsLoading,
    error: notificationsError,
    toggleTopicSubscription,
    sendTestNotification,
    getAvailableTopics
  } = useNotifications(user?.uid)

  // Estados locales
  const [telegramEnabled, setTelegramEnabled] = useState(false)
  const [telegramToken, setTelegramToken] = useState("")
  const [telegramChatId, setTelegramChatId] = useState("")
  const [notificationsEnabled, setNotificationsEnabled] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)

  // Cargar preferencias guardadas al montar el componente
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user?.uid) return

      const prefs = await OfflineCache.getUserPreferences(user.uid)
      if (prefs) {
        setNotificationsEnabled(prefs.notificationsEnabled ?? true)
        setTelegramEnabled(prefs.telegramEnabled ?? false)
        setTelegramToken(prefs.telegramToken ?? "")
        setTelegramChatId(prefs.telegramChatId ?? "")
      }
    }

    loadPreferences()
  }, [user])

  // Guardar configuración
  const handleSaveSettings = async () => {
    if (!user) {
      toast.error(language === "en" ? "You must be logged in" : "Debes iniciar sesión")
      return
    }

    // Validar campos de Telegram si está habilitado
    if (telegramEnabled && (!telegramToken || !telegramChatId)) {
      toast.error(
        language === "en"
          ? "Please complete all Telegram fields"
          : "Por favor completa todos los campos de Telegram"
      )
      return
    }

    setIsSaving(true)

    try {
      if (telegramEnabled && telegramChatId) {
        toast.info(
          language === "en"
            ? "Telegram access is being hardened. Your Telegram details are stored locally for now and channel membership will be handled from the secure backend."
            : "El acceso de Telegram se está reforzando. Tus datos de Telegram se guardan localmente por ahora y la membresía a canales se gestionará desde el backend seguro."
        )
      }

      // Guardar preferencias en IndexedDB (offline cache)
      await OfflineCache.saveUserPreferences({
        id: `pref-${user.uid}`,
        userId: user.uid,
        notificationsEnabled,
        telegramEnabled,
        telegramToken: telegramEnabled ? telegramToken : undefined,
        telegramChatId: telegramEnabled ? telegramChatId : undefined,
        discordEnabled: false,
        subscribedTopics: getAvailableTopics()
          .filter(t => t.enabled)
          .map(t => t.topic),
        language: language === "en" ? "en" : "es",
        theme: "dark",
        updatedAt: Date.now()
      })

      toast.success(
        language === "en"
          ? "Settings saved successfully"
          : "Configuración guardada correctamente"
      )
    } catch (error) {
      console.error("Error saving settings:", error)
      toast.error(
        language === "en"
          ? `Error saving settings: ${error instanceof Error ? error.message : "Unknown error"}`
          : `Error al guardar configuración: ${error instanceof Error ? error.message : "Error desconocido"}`
      )
    } finally {
      setIsSaving(false)
    }
  }

  // Probar conexión de Telegram
  const handleTestTelegramConnection = async () => {
    if (!telegramToken || !telegramChatId) {
      toast.error(
        language === "en"
          ? "Please enter both Bot Token and Chat ID"
          : "Por favor ingresa el Token del Bot y el Chat ID"
      )
      return
    }

    setIsTesting(true)

    try {
      // TODO: Conectar con backend cuando esté disponible
      // const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/telegram/send-test`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ botToken: telegramToken, chatId: telegramChatId })
      // })
      //
      // const data = await response.json()
      //
      // if (data.success) {
      //   toast.success(language === "en" ? "Test message sent successfully! Check your Telegram." : "Mensaje de prueba enviado! Revisa tu Telegram.")
      // } else {
      //   throw new Error(data.message)
      // }

      // Simulación temporal
      await new Promise(resolve => setTimeout(resolve, 1500))
      toast.success(
        language === "en"
          ? "✅ Test message sent successfully! Check your Telegram chat."
          : "✅ Mensaje de prueba enviado correctamente! Revisa tu chat de Telegram."
      )
    } catch (error) {
      console.error("Error testing Telegram connection:", error)
      toast.error(
        `${language === "en" ? "Error" : "Error"}: ${error instanceof Error ? error.message : "Connection failed"}`
      )
    } finally {
      setIsTesting(false)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <InternalNavbar />

      <div className="container mx-auto px-4 py-8 max-w-4xl font-poppins">
        <div className="space-y-6">
          {/* Page Header */}
          <div className="space-y-2">
            <h1 className="text-4xl font-bold flex items-center gap-3 text-foreground">
              <SettingsIcon className="w-9 h-9 text-primary" />
              {language === "en" ? "Settings" : "Configuración"}
            </h1>
            <p className="text-base text-muted-foreground">
              {language === "en"
                ? "Configure your notification preferences and integrations"
                : "Configura tus preferencias de notificación e integraciones"}
            </p>
          </div>

          {/* Push Notifications */}
          <Card className="border-border/50 shadow-soft">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Bell className="w-5 h-5 text-primary" />
                {language === "en" ? "Push Notifications" : "Notificaciones Push"}
              </CardTitle>
              <CardDescription>
                {language === "en"
                  ? "Manage browser push notifications for real-time signal alerts"
                  : "Gestiona las notificaciones push del navegador para alertas de señales en tiempo real"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Permission Status */}
              <div className="flex items-center justify-between p-4 rounded-lg border border-border/50 bg-muted/30">
                <div className="space-y-1">
                  <Label className="text-base font-medium">
                    {language === "en" ? "Notification Permission" : "Permiso de Notificaciones"}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {language === "en"
                      ? "Browser notification access status"
                      : "Estado de acceso a notificaciones del navegador"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {permission === 'granted' ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      <span className="text-sm font-medium text-emerald-500">
                        {language === "en" ? "Granted" : "Concedido"}
                      </span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-muted-foreground" />
                      <span className="text-sm font-medium text-muted-foreground">
                        {language === "en" ? "Not granted" : "No concedido"}
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Test Notification */}
              {token && (
                <div className="flex items-center justify-between p-4 rounded-lg border border-border/50 bg-muted/30">
                  <div className="space-y-1">
                    <Label className="text-base font-medium">
                      {language === "en" ? "Test Notification" : "Notificación de Prueba"}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      {language === "en"
                        ? "Send a test notification to verify setup"
                        : "Envía una notificación de prueba para verificar la configuración"}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={sendTestNotification}
                    disabled={notificationsLoading}
                    className="gap-2"
                  >
                    <TestTube className="w-4 h-4" />
                    {language === "en" ? "Send Test" : "Enviar Prueba"}
                  </Button>
                </div>
              )}

              {/* Error Display */}
              {notificationsError && (
                <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-destructive">{notificationsError}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Topics */}
          <Card className="border-border/50 shadow-soft">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Bell className="w-5 h-5 text-primary" />
                {language === "en" ? "Notification Topics" : "Temas de Notificación"}
              </CardTitle>
              <CardDescription>
                {language === "en"
                  ? "Subscribe to specific signal types and receive targeted notifications"
                  : "Suscríbete a tipos específicos de señales y recibe notificaciones dirigidas"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                {getAvailableTopics().map((topic) => (
                  <div
                    key={topic.topic}
                    className="flex items-center justify-between p-4 border border-border/50 rounded-lg hover:bg-muted/30 transition-colors"
                  >
                    <div className="space-y-1 flex-1">
                      <Label className="text-sm font-medium cursor-pointer">{topic.name}</Label>
                      <p className="text-xs text-muted-foreground">{topic.description}</p>
                    </div>
                    <Switch
                      checked={topic.enabled}
                      onCheckedChange={() => toggleTopicSubscription(topic.topic)}
                      disabled={notificationsLoading}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Plan Status Card */}
          <Card className="border-border/50 shadow-soft">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Crown className="w-5 h-5 text-primary" />
                {language === "en" ? "Your Plan" : "Tu Plan"}
              </CardTitle>
              <CardDescription>
                {language === "en"
                  ? "Manage your subscription and plan features"
                  : "Gestiona tu suscripción y funciones del plan"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Plan Badge */}
              <div className="flex items-center gap-3">
                {licenseType === "free" && (
                  <Badge variant="outline" className="border-border text-muted-foreground">
                    {language === "en" ? "Free" : "Gratuito"}
                  </Badge>
                )}
                {isTrial && isActive && (
                  <>
                    <Badge variant="outline" className="border-primary/30 text-primary bg-primary/10">
                      {language === "en" ? "Free Trial" : "Prueba Gratuita"}
                    </Badge>
                    {daysUntilExpiry !== null && (
                      <Badge variant="outline" className="border-emerald-500/30 text-emerald-500 bg-emerald-500/10">
                        {daysUntilExpiry} {language === "en" ? "days left" : "días restantes"}
                      </Badge>
                    )}
                  </>
                )}
                {licenseType === "premium" && isActive && (
                  <Badge className="bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0">
                    {language === "en" ? "Premium" : "Premium"}
                  </Badge>
                )}
                {licenseType === "enterprise" && isActive && (
                  <Badge className="bg-gradient-to-r from-primary to-secondary text-primary-foreground border-0">
                    {language === "en" ? "Enterprise" : "Empresarial"}
                  </Badge>
                )}
                {isExpired && (
                  <Badge variant="outline" className="border-destructive/30 text-destructive bg-destructive/10">
                    {language === "en" ? "Expired" : "Expirado"}
                  </Badge>
                )}
              </div>

              {/* Activate Trial Button (for free users) */}
              {(!license || (!isActive && !isExpired)) && (
                <div className="p-4 rounded-lg border border-primary/50 bg-primary/5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-foreground mb-1">
                        {language === "en" ? "Activate Free Trial" : "Activar Prueba Gratuita"}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === "en"
                          ? "Free-trial activation is temporarily paused while entitlement changes move to the secure backend."
                          : "La activación de prueba gratuita está pausada temporalmente mientras los cambios de permisos se mueven al backend seguro."}
                      </p>
                    </div>
                    <Button
                      disabled
                      variant="outline"
                      className="gap-2 whitespace-nowrap border-primary/50 text-primary hover:bg-primary/10"
                    >
                      <Zap className="w-4 h-4" />
                      {language === "en" ? "Trial Unavailable" : "Prueba No Disponible"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Trial Active - Upgrade to Premium */}
              {isTrial && isActive && (
                <div className="p-4 rounded-lg border border-primary/50 bg-primary/5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-foreground mb-1">
                        {language === "en" ? "Upgrade to Premium" : "Actualizar a Premium"}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === "en"
                          ? "Get access to the private Telegram channel and all premium features"
                          : "Obtén acceso al canal privado de Telegram y todas las funciones premium"}
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate("/payment")}
                      className="gap-2 whitespace-nowrap"
                    >
                      {language === "en" ? "Upgrade Now" : "Actualizar Ahora"}
                      <Crown className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Trial Expired - Upgrade to Premium */}
              {isTrial && isExpired && (
                <div className="p-4 rounded-lg border border-destructive/50 bg-destructive/5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-foreground mb-1">
                        {language === "en" ? "Trial Expired" : "Prueba Expirada"}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === "en"
                          ? "Your free trial has ended. Upgrade to Premium to continue receiving signals"
                          : "Tu prueba gratuita ha finalizado. Actualiza a Premium para continuar recibiendo señales"}
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate("/payment")}
                      className="gap-2 whitespace-nowrap"
                    >
                      {language === "en" ? "Upgrade to Premium" : "Adquirir Servicio Premium"}
                      <Crown className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Premium Active - Join Private Chat */}
              {licenseType === "premium" && isActive && (
                <div className="p-4 rounded-lg border border-primary/50 bg-primary/5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-foreground mb-1">
                        {language === "en" ? "Join Private Telegram Channel" : "Unirse al Canal Privado de Telegram"}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {language === "en"
                          ? "Get added to the private Telegram channel to receive all premium signals in real-time"
                          : "Únete al canal privado de Telegram para recibir todas las señales premium en tiempo real"}
                      </p>
                    </div>
                    <Button
                      onClick={async () => {
                        // TODO: Implement join private chat function
                        toast.info(language === "en" ? "Feature coming soon!" : "¡Función próximamente!")
                      }}
                      variant="outline"
                      className="gap-2 whitespace-nowrap border-primary/50 text-primary hover:bg-primary/10"
                    >
                      <MessageSquare className="w-4 h-4" />
                      {language === "en" ? "Join Private Chat" : "Unirse al Chat Privado"}
                    </Button>
                  </div>
                </div>
              )}

              {/* Connect Telegram Button (for all users, but different messaging) */}
              <div className="p-4 rounded-lg border border-primary/30 bg-primary/5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="text-base font-semibold text-foreground mb-1 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4 text-primary" />
                      {language === "en" ? "Connect Telegram" : "Conectar Telegram"}
                    </h4>
                    <p className="text-sm text-foreground/70">
                      {licenseType === "free" || (!license && !isActive)
                        ? language === "en"
                          ? "Join our public Telegram channel to receive signals during your free trial"
                          : "Únete a nuestro canal público de Telegram para recibir señales durante tu prueba gratuita"
                        : licenseType === "premium" && isActive
                        ? language === "en"
                          ? "Join the private Telegram channel to receive all premium signals"
                          : "Únete al canal privado de Telegram para recibir todas las señales premium"
                        : language === "en"
                        ? "Join our Telegram channel to receive signals"
                        : "Únete a nuestro canal de Telegram para recibir señales"}
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      window.open('https://t.me/riskopulse_signals', '_blank')
                    }}
                    className="gap-2 whitespace-nowrap bg-primary hover:bg-primary/90 text-primary-foreground"
                    size="sm"
                  >
                    <MessageSquare className="w-4 h-4" />
                    {language === "en" ? "Join Channel" : "Unirse al Canal"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* License Management */}
          {user && <LicenseManager userId={user.uid} />}

          {/* Telegram Integration - Premium Feature */}
          {features.telegramIntegration ? (
            <Card id="telegram-section" className="border-border/50 shadow-soft">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <MessageSquare className="w-5 h-5 text-[#0088cc]" />
                  {language === "en" ? "Telegram Integration" : "Integración con Telegram"}
                </CardTitle>
                <CardDescription>
                  {language === "en"
                    ? "Connect your personal Telegram bot to receive signal notifications directly in your chat"
                    : "Conecta tu bot personal de Telegram para recibir notificaciones de señales directamente en tu chat"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
              {/* Enable Telegram Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border border-primary/30 bg-primary/5 shadow-sm">
                <div className="space-y-1">
                  <Label className="text-base font-medium text-foreground">
                    {language === "en" ? "Enable Telegram Notifications" : "Activar Notificaciones de Telegram"}
                  </Label>
                  <p className="text-sm text-foreground/70">
                    {language === "en"
                      ? "Receive signals directly in your Telegram chat"
                      : "Recibe señales directamente en tu chat de Telegram"}
                  </p>
                </div>
                <Switch
                  checked={telegramEnabled}
                  onCheckedChange={setTelegramEnabled}
                />
              </div>

              {/* Telegram Configuration (shown when enabled) */}
              {telegramEnabled && (
                <div className="space-y-4 p-4 rounded-lg border border-primary/20 bg-primary/5 shadow-sm">
                  {/* Bot Token Input */}
                  <div className="space-y-2">
                    <Label htmlFor="telegram-token" className="text-base font-medium flex items-center gap-2">
                      {language === "en" ? "Bot Token" : "Token del Bot"}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="telegram-token"
                      type="password"
                      placeholder={language === "en" ? "123456789:ABCdefGHIjklMNOpqrsTUVwxyz" : "123456789:ABCdefGHIjklMNOpqrsTUVwxyz"}
                      value={telegramToken}
                      onChange={(e) => setTelegramToken(e.target.value)}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground flex items-start gap-2">
                      <HelpCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      <span>
                        {language === "en"
                          ? "Create a bot with @BotFather on Telegram and copy the token here"
                          : "Crea un bot con @BotFather en Telegram y copia el token aquí"}
                      </span>
                    </p>
                  </div>

                  {/* Chat ID Input */}
                  <div className="space-y-2">
                    <Label htmlFor="telegram-chat-id" className="text-base font-medium flex items-center gap-2">
                      {language === "en" ? "Chat ID" : "ID del Chat"}
                      <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="telegram-chat-id"
                      type="text"
                      placeholder={language === "en" ? "@my_channel or -1001234567890" : "@mi_canal o -1001234567890"}
                      value={telegramChatId}
                      onChange={(e) => setTelegramChatId(e.target.value)}
                      className="font-mono text-sm"
                    />
                    <div className="text-xs text-muted-foreground space-y-1">
                      <p className="flex items-start gap-2">
                        <HelpCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                        <span>
                          {language === "en"
                            ? "How to get your Chat ID:"
                            : "Cómo obtener tu Chat ID:"}
                        </span>
                      </p>
                      <ol className="list-decimal list-inside ml-5 space-y-0.5 text-muted-foreground/80">
                        <li>
                          {language === "en"
                            ? "Send a message to your bot"
                            : "Envía un mensaje a tu bot"}
                        </li>
                        <li>
                          {language === "en"
                            ? "Visit: "
                            : "Visita: "}
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            api.telegram.org/bot&lt;TOKEN&gt;/getUpdates
                          </code>
                        </li>
                        <li>
                          {language === "en"
                            ? 'Look for "chat":{"id":123456789}'
                            : 'Busca "chat":{"id":123456789}'}
                        </li>
                      </ol>
                    </div>
                  </div>

                  {/* Connection Status */}
                  <div className={`flex items-center gap-2 p-3 rounded-md ${
                    telegramToken && telegramChatId 
                      ? 'bg-emerald-500/10 border border-emerald-500/30' 
                      : 'bg-primary/10 border border-primary/30'
                  }`}>
                    {telegramToken && telegramChatId ? (
                      <>
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        <span className="text-sm font-medium text-emerald-400">
                          {language === "en" ? "Configuration complete" : "Configuración completa"}
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-primary">
                          {language === "en" ? "Incomplete configuration" : "Configuración incompleta"}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Test Connection Button */}
                  <Button
                    onClick={handleTestTelegramConnection}
                    disabled={!telegramToken || !telegramChatId || isTesting}
                    variant="outline"
                    className="w-full gap-2"
                  >
                    {isTesting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {language === "en" ? "Testing connection..." : "Probando conexión..."}
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {language === "en" ? "Send Test Message" : "Enviar Mensaje de Prueba"}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="border-primary/30 bg-primary/5 shadow-soft">
            <CardContent className="p-8">
              <div className="flex flex-col items-center justify-center text-center space-y-4">
                <div className="p-4 rounded-full bg-primary/10">
                  <Lock className="w-8 h-8 text-primary" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-primary">
                    {language === "en" ? "Premium Feature" : "Función Premium"}
                  </h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    {language === "en"
                      ? "Telegram integration is available for Premium and Enterprise users. Activate a license to unlock this feature."
                      : "La integración de Telegram está disponible para usuarios Premium y Enterprise. Activa una licencia para desbloquear esta función."}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

          {/* PWA Installation */}
          <Card className="border-border/50 shadow-soft">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Smartphone className="w-5 h-5 text-primary" />
                {language === "en" ? "Progressive Web App" : "Aplicación Web Progresiva"}
              </CardTitle>
              <CardDescription>
                {language === "en"
                  ? "Install Risko Pulse for a native-like experience with offline support"
                  : "Instala Risko Pulse para una experiencia similar a una app nativa con soporte offline"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg border border-primary/30 bg-primary/5 shadow-sm">
                <div className="space-y-1">
                  <Label className="text-base font-medium text-foreground">
                    {language === "en" ? "Install Risko Pulse" : "Instalar Risko Pulse"}
                  </Label>
                  <p className="text-sm text-foreground/70">
                    {language === "en"
                      ? "Add to your home screen for quick access"
                      : "Agrega a tu pantalla de inicio para acceso rápido"}
                  </p>
                </div>
                <PWAInstallButton size="default" />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSaveSettings}
              disabled={isSaving}
              size="lg"
              className="gap-2 min-w-[200px]"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {language === "en" ? "Saving..." : "Guardando..."}
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {language === "en" ? "Save Settings" : "Guardar Configuración"}
                </>
              )}
            </Button>
          </div>

          {/* Account Section */}
          <Card className="border-border/50 shadow-soft">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-xl">
                <User className="w-5 h-5 text-primary" />
                {language === "en" ? "Account" : "Cuenta"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {user && (
                  <>
                    <div className="space-y-2">
                      <Label className="text-sm text-muted-foreground">
                        {language === "en" ? "Email Address" : "Correo Electrónico"}
                      </Label>
                      <p className="text-base font-medium text-foreground">
                        {user.email}
                      </p>
                    </div>
                    {user.displayName && (
                      <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">
                          {language === "en" ? "Display Name" : "Nombre para Mostrar"}
                        </Label>
                        <p className="text-base font-medium text-foreground">
                          {user.displayName}
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
